import { NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { BrandingService } from '../_core/branding.service';
import type { MeterHelpTypeId } from '../_core/branding-assets';

export type { MeterHelpTypeId };

export interface MeterHelpType {
  id: MeterHelpTypeId;
  title: string;
  subtitle: string;
  imageSrc: string;
  width: number;
  height: number;
  helpParagraphs: string[];
}

const METER_HELP_BASE: readonly Omit<MeterHelpType, 'imageSrc'>[] = [
  {
    id: 'modern',
    title: 'Contador moderno',
    subtitle: 'Contador interno',
    width: 572,
    height: 887,
    helpParagraphs: [
      'Lee los dígitos negros: son los metros cúbicos (m³).',
      'Ignora los dígitos rojos; representan litros y no se incluyen en la lectura.',
    ],
  },
  {
    id: 'clock',
    title: 'Contador antiguo',
    subtitle: 'Reloj con manecillas',
    width: 590,
    height: 880,
    helpParagraphs: [
      'Lee las esferas en este orden: x1000 → x100 → x10 → x1.',
      'Si la aguja está entre dos cifras, anota la menor.',
      'No es necesario leer el reloj x0,1 (litros).',
    ],
  },
  {
    id: 'digital',
    title: 'Contador digital',
    subtitle: 'Pantalla digital',
    width: 574,
    height: 867,
    helpParagraphs: [
      'Anota la parte entera que aparece antes del punto o la coma decimal.',
      'Descarta los decimales (litros); no se incluyen en la lectura.',
    ],
  },
];

@Component({
  selector: 'app-send-reading-help-dialog',
  imports: [ButtonModule, DialogModule, NgOptimizedImage],
  templateUrl: './send-reading-help-dialog.html',
  styleUrl: './send-reading-help-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SendReadingHelpDialog {
  private readonly $branding = inject(BrandingService);

  readonly visible = signal(false);
  readonly selectedTypeId = signal<MeterHelpTypeId | null>(null);

  readonly blogUrl =
    'https://www.gomezgroupmetering.com/blog/2024/11/20/quieres-saber-como-leer-un-contador-de-agua/';

  readonly meterTypes = computed<readonly MeterHelpType[]>(() => {
    const images = this.$branding.meterReadingHelpImages();
    return METER_HELP_BASE.flatMap((type) => {
      const imageSrc = images[type.id];
      return imageSrc ? [{ ...type, imageSrc }] : [];
    });
  });

  readonly selectedType = computed(() => {
    const id = this.selectedTypeId();
    return this.meterTypes().find((type) => type.id === id) ?? null;
  });

  open(): void {
    this.selectedTypeId.set(null);
    this.visible.set(true);
  }

  onVisibleChange(value: boolean): void {
    this.visible.set(value);
    if (!value) {
      this.selectedTypeId.set(null);
    }
  }

  selectType(id: MeterHelpTypeId): void {
    this.selectedTypeId.set(id);
  }

  clearSelection(): void {
    this.selectedTypeId.set(null);
  }
}
